// The sandbox session label — persistent, on every screen, not dismissible.
//
// It reads /v1/me, not the environments endpoint: the label has to appear for
// EVERY user of a sandbox tenant, including one with no settings authority,
// and /v1/admin/environments is permission-gated. It renders nothing in a
// production session, which is why the shell can mount it unconditionally.

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { unwrapEnvelope } from "@/api/errors";
import { platformGraph } from "@/api/platform-graph";
import type { paths } from "@/api/schema.generated";

type SessionEnvironment =
  paths["/v1/me"]["get"]["responses"][200]["content"]["application/json"]["data"]["environment"];

const sessionEnvironmentQueryKey = ["me", "environment"] as const;

async function fetchSessionEnvironment(): Promise<SessionEnvironment> {
  return (await unwrapEnvelope(platformGraph.GET("/v1/me"))).environment;
}

export function EnvironmentBanner(): React.ReactElement | null {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: sessionEnvironmentQueryKey,
    queryFn: fetchSessionEnvironment,
  });
  // Belirsizlik ETİKETE DOĞRU düşer: /v1/me cevaplamazsa oturumun sandbox
  // olmadığını bilmiyoruz, ve etiketsiz bir sandbox oturumu kullanıcıya örnek
  // veriyi üretim sanıyla gösterir. Sandbox olduğunu İDDİA ETMEDEN, okunamadığını
  // söyleriz.
  if (query.isError) {
    return (
      <div
        role="status"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b-[3px] border-[var(--state-warning)] bg-[var(--bg-subtle)] px-4 py-2 text-sm text-[var(--fg-primary)] md:px-7"
      >
        <span className="font-medium">{t("admin_environments.banner_unknown")}</span>
      </div>
    );
  }
  if (query.data?.kind !== "sandbox") return null;
  const back = query.data.production_sign_in_url;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b-[3px] border-[var(--state-warning)] bg-[var(--bg-subtle)] px-4 py-2 text-sm text-[var(--fg-primary)] md:px-7"
    >
      <span className="font-medium">{t("admin_environments.banner")}</span>
      {back !== null && (
        <a href={back} className="underline">
          {t("admin_environments.banner_back")}
        </a>
      )}
    </div>
  );
}
